import { Box, Fade, Typography } from "@mui/material"
import { LinkRounded } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { SortingState } from "@tanstack/react-table"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"

import { AddProductAssociationDialog } from "../components/AssociateProductDialog/AddProductAssociationDialog"
import { EditProductAssociationDialog } from "../components/AssociateProductDialog/EditProductAssociationDialog"
import { CustomerProductAssociationsTable } from "../components/CustomerProductAssociationsTable"
import { CustomerProductFilterChips } from "../components/CustomerProductFilterChips"
import { CustomerProductFilterDrawer } from "../components/CustomerProductFilterDrawer"
import { CustomerProductsSearchBar } from "../components/CustomerProductsSearchBar"
import { useCustomerProductList } from "../hooks/useCustomerProductList"
import { useDeleteCompanyProductAssociation } from "../hooks/useDeleteCompanyProductAssociation"
import type { CompanyProductAssociationListItem } from "../hooks/useGetCompanyProductAssociations"
import { useGetLocationListByCompany } from "../hooks/useGetLocationListByCompany"
import type { CustomerProductAssociationsFilters } from "../stores/useCustomerProductAssociationsStore"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import { PermissionActionType } from "@/constants/permissions"
import type { ComputedProductStatus } from "@/graphql/codegen/graphql"
import { CompanyProductAssociationSortField, PriorityStatus, SortingOrder } from "@/graphql/codegen/graphql"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { COMPANY_EDIT_ROUTE } from "@/screens/Companies/constants"

const COLUMN_SORT_FIELD_MAP: Partial<Record<string, CompanyProductAssociationSortField>> = {
  productId: CompanyProductAssociationSortField.ProductId,
  product_companyProductDescription: CompanyProductAssociationSortField.AkroDesc,
  customerDescription: CompanyProductAssociationSortField.CustomerDescription,
  locationCustomerCode: CompanyProductAssociationSortField.CustomerCode,
  product_company_name: CompanyProductAssociationSortField.Supplier,
  shipFrom: CompanyProductAssociationSortField.ShipFrom,
  shipTo: CompanyProductAssociationSortField.ShipTo,
  stock: CompanyProductAssociationSortField.Stock,
  priority: CompanyProductAssociationSortField.Priority,
}

export const CustomerProductsContainer: FC = () => {
  const { companyId } = useParams({ from: COMPANY_EDIT_ROUTE })

  const [isAssociateDialogOpen, setIsAssociateDialogOpen] = useState(false)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [editingAssociation, setEditingAssociation] = useState<CompanyProductAssociationListItem | undefined>()
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null)
  const [selectedPriorityStatuses, setSelectedPriorityStatuses] = useState<PriorityStatus[]>([PriorityStatus.Primary])
  const [selectedComputedStatuses, setSelectedComputedStatuses] = useState<ComputedProductStatus[]>([])
  const [sorting, setSorting] = useState<SortingState>([])

  const { hasPermissions } = usePermissionManager()
  const canEdit = hasPermissions([PermissionActionType.CUSTOMER_MAINTENANCE])

  const { deleteAssociation } = useDeleteCompanyProductAssociation()
  const { locations, refetchAssociatedLocations } = useGetLocationListByCompany(companyId)

  const filters: CustomerProductAssociationsFilters = useMemo(() => {
    const sortEntry = sorting[0]
    const sortField = sortEntry ? COLUMN_SORT_FIELD_MAP[sortEntry.id] : undefined
    return {
      companyId,
      query: searchQuery,
      priorityStatuses: selectedPriorityStatuses,
      computedProductStatuses: selectedComputedStatuses,
      sort: sortField
        ? { field: sortField, direction: sortEntry.desc ? SortingOrder.Desc : SortingOrder.Asc }
        : undefined,
      shipToLocationId: selectedLocationId ?? undefined,
    }
  }, [companyId, searchQuery, selectedPriorityStatuses, selectedComputedStatuses, sorting, selectedLocationId])

  const { data, isFetching, tableContainerRef, hasItems, handleScroll, refetch } = useCustomerProductList(filters)

  const isClearButtonEnabled = selectedComputedStatuses.length > 0 || selectedPriorityStatuses.length > 0

  const handleAssociateDialogClose = useCallback(
    (shouldRefetch?: boolean) => {
      setIsAssociateDialogOpen(false)
      if (shouldRefetch) {
        refetch()
        refetchAssociatedLocations()
      }
    },
    [refetch, refetchAssociatedLocations]
  )

  const handleEditAssociation = useCallback((item: CompanyProductAssociationListItem) => {
    setEditingAssociation(item)
  }, [])

  const handleEditDialogClose = useCallback(
    (shouldRefetch?: boolean) => {
      setEditingAssociation(undefined)
      if (shouldRefetch) {
        refetch()
        refetchAssociatedLocations()
      }
    },
    [refetch, refetchAssociatedLocations]
  )

  const handleRemoveAssociation = useCallback(
    async (item: CompanyProductAssociationListItem): Promise<boolean> => {
      const success = await deleteAssociation(item.companyProductAssociationId)
      if (success) {
        refetch()
        refetchAssociatedLocations()
      }
      return success
    },
    [deleteAssociation, refetch, refetchAssociatedLocations]
  )

  const handleFilterDrawerOpen = useCallback(() => setIsFilterDrawerOpen(true), [])
  const handleFilterDrawerClose = useCallback(() => setIsFilterDrawerOpen(false), [])

  const handleClearFilters = () => {
    setSelectedComputedStatuses([])
    setSelectedPriorityStatuses([])
  }

  const handleRemovePriorityStatus = (status: PriorityStatus) => {
    setSelectedPriorityStatuses((prev) => prev.filter((priorityStatus) => priorityStatus !== status))
  }

  const handleRemoveComputedStatus = (status: ComputedProductStatus) => {
    setSelectedComputedStatuses((prev) => prev.filter((computedStatus) => computedStatus !== status))
  }

  return (
    <Box className='flex h-full flex-col gap-4'>
      <Typography variant='h5' className='mt-2 text-primary'>
        Associated Products
      </Typography>

      <CustomerProductsSearchBar
        locations={locations}
        searchQuery={searchQuery}
        selectedLocationId={selectedLocationId}
        onSearchChange={setSearchQuery}
        onLocationChange={setSelectedLocationId}
        onFilterOpen={handleFilterDrawerOpen}
        onAssociateProduct={() => setIsAssociateDialogOpen(true)}
        canEdit={canEdit}
      />

      <CustomerProductFilterChips
        selectedComputedStatuses={selectedComputedStatuses}
        selectedPriorityStatuses={selectedPriorityStatuses}
        onRemoveComputedStatus={handleRemoveComputedStatus}
        onRemovePriorityStatus={handleRemovePriorityStatus}
      />

      {!hasItems && !isFetching && !searchQuery ? (
        <Fade in unmountOnExit timeout={500}>
          <InformationMissingAlert
            title='Start by associating a product'
            action={
              canEdit && (
                <Button
                  color='inherit'
                  size='small'
                  appearance='outlined'
                  className='text-primary'
                  startIcon={<LinkRounded />}
                  onClick={() => setIsAssociateDialogOpen(true)}
                  data-testid='empty-state-associate-button'
                >
                  Associate Product
                </Button>
              )
            }
          />
        </Fade>
      ) : (
        <Box className='min-h-0 grow overflow-hidden'>
          <CustomerProductAssociationsTable
            items={data}
            isFetching={isFetching}
            sorting={sorting}
            onSortingChange={setSorting}
            tableContainerRef={tableContainerRef}
            onScroll={handleScroll}
            onEdit={handleEditAssociation}
            onRemove={handleRemoveAssociation}
            canEdit={canEdit}
          />
        </Box>
      )}

      <CustomerProductFilterDrawer
        open={isFilterDrawerOpen}
        selectedComputedStatuses={selectedComputedStatuses}
        selectedPriorityStatuses={selectedPriorityStatuses}
        isClearButtonEnabled={isClearButtonEnabled}
        onClose={handleFilterDrawerClose}
        onComputedStatusChange={setSelectedComputedStatuses}
        onPriorityStatusChange={setSelectedPriorityStatuses}
        onClear={handleClearFilters}
      />

      {isAssociateDialogOpen && (
        <AddProductAssociationDialog companyId={companyId} onClose={handleAssociateDialogClose} />
      )}

      {editingAssociation && (
        <EditProductAssociationDialog association={editingAssociation} onClose={handleEditDialogClose} />
      )}
    </Box>
  )
}
